import React, { useCallback, useEffect, useState } from 'react';
import { ClipboardCopy, Hash, Loader2, Pencil, Plus, Trash2 } from 'lucide-react';
import { Button } from '@components/Button';
import { Dialog, ConfirmDialog } from '@components/Dialog';
import { ApiErrorModal } from '@components/ApiErrorModal';
import { usePermissions } from '@hooks/usePermissions';
import socialService from '@services/socialService';
import type { HashtagGroup, Network, SaveHashtagGroupRequest } from '@/types/social';
import { NETWORKS, NETWORK_LABELS } from '@/types/social';
import { extractHashtags } from '@/utils/social';
import { NetworkIcon } from '@components/social/SocialBits';

/**
 * Grupos de hashtags salvos, inseridos com um clique no compositor (legenda ou 1º comentário).
 * Documentação: src/docs/redes-sociais/hashtags.md.
 */
export const SocialHashtagsView: React.FC = () => {
  const [groups, setGroups] = useState<HashtagGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const [form, setForm] = useState<{ id: number | null; data: SaveHashtagGroupRequest } | null>(null);
  const [toDelete, setToDelete] = useState<HashtagGroup | null>(null);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState<number | null>(null);
  const { checkPermission } = usePermissions();
  const canWrite = checkPermission('social.write').hasPermission;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setGroups(await socialService.listHashtagGroups(true));
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const save = async () => {
    if (!form) return;
    setSaving(true);
    try {
      await socialService.saveHashtagGroup(form.id, form.data);
      setForm(null);
      await load();
    } catch (err) {
      setError(err);
    } finally {
      setSaving(false);
    }
  };

  const typed = form ? extractHashtags(form.data.hashtags.split(/[\s,;]+/).map((h) => (h && !h.startsWith('#') ? `#${h}` : h)).join(' ')).length : 0;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-small text-slate-500">
          No compositor, use "+ Grupo de hashtags…" na legenda ou no 1º comentário. O Instagram aceita até 30 hashtags por post (legenda + 1º comentário).
        </p>
        {loading && <Loader2 size={16} className="animate-spin text-slate-400" />}
        {canWrite && (
          <Button size="sm" icon={<Plus />} onClick={() => setForm({ id: null, data: { name: '', hashtags: '', network: null, is_active: true } })}>
            Novo grupo
          </Button>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
        {groups.map((g) => (
          <div key={g.id} className={`card p-3 space-y-2 ${g.is_active ? '' : 'opacity-50'}`}>
            <div className="flex items-center gap-2">
              <Hash size={14} className="text-slate-400" />
              <p className="font-semibold text-slate-800 flex-1">{g.name}</p>
              {g.network && <NetworkIcon network={g.network} />}
              <span className={g.count > 30 ? 'badge-danger' : 'badge-neutral'}>{g.count}</span>
            </div>
            <p className="text-sm text-sky-700 break-words">{g.hashtags}</p>
            {!g.is_active && <p className="text-xs text-slate-400">inativo (não aparece no compositor)</p>}
            <div className="flex gap-1">
              <Button
                size="sm"
                variant="outline"
                icon={<ClipboardCopy />}
                onClick={async () => {
                  await navigator.clipboard.writeText(g.hashtags).catch(() => undefined);
                  setCopied(g.id);
                  setTimeout(() => setCopied(null), 1500);
                }}
              >
                {copied === g.id ? 'Copiado!' : 'Copiar'}
              </Button>
              {canWrite && (
                <>
                  <Button
                    size="sm"
                    variant="outline"
                    icon={<Pencil />}
                    onClick={() => setForm({ id: g.id, data: { name: g.name, hashtags: g.hashtags, network: g.network ?? null, sort_order: g.sort_order, is_active: g.is_active } })}
                  >
                    Editar
                  </Button>
                  <Button size="sm" variant="outline" icon={<Trash2 />} onClick={() => setToDelete(g)}>
                    {''}
                  </Button>
                </>
              )}
            </div>
          </div>
        ))}
        {groups.length === 0 && !loading && <p className="text-sm text-slate-500">Nenhum grupo ainda.</p>}
      </div>

      <Dialog
        isOpen={!!form}
        onClose={() => setForm(null)}
        title={form?.id ? 'Editar grupo' : 'Novo grupo de hashtags'}
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setForm(null)}>
              Cancelar
            </Button>
            <Button loading={saving} onClick={save}>
              Salvar
            </Button>
          </div>
        }
      >
        {form && (
          <div className="space-y-3">
            <input className="input-field w-full" placeholder="Nome (Closet, Cozinha, Marca...)" value={form.data.name} onChange={(e) => setForm({ ...form, data: { ...form.data, name: e.target.value } })} />
            <textarea
              className="input-field w-full min-h-[100px]"
              placeholder="closetplanejado, #organizacao #casaorganizada"
              value={form.data.hashtags}
              onChange={(e) => setForm({ ...form, data: { ...form.data, hashtags: e.target.value } })}
            />
            <p className={`text-xs ${typed > 30 ? 'text-red-600' : 'text-slate-500'}`}>{typed} hashtag(s) — com ou sem #, separadas por espaço, vírgula ou linha.</p>
            <div className="flex items-center gap-3">
              <select
                className="select-field w-48"
                value={form.data.network ?? ''}
                onChange={(e) => setForm({ ...form, data: { ...form.data, network: (e.target.value || null) as Network | null } })}
              >
                <option value="">Qualquer rede</option>
                {NETWORKS.map((n) => (
                  <option key={n} value={n}>
                    {NETWORK_LABELS[n]}
                  </option>
                ))}
              </select>
              <label className="inline-flex items-center gap-1.5 text-sm text-slate-600">
                <input type="checkbox" className="checkbox-field" checked={form.data.is_active ?? true} onChange={(e) => setForm({ ...form, data: { ...form.data, is_active: e.target.checked } })} />
                Ativo
              </label>
            </div>
          </div>
        )}
      </Dialog>

      <ConfirmDialog
        isOpen={!!toDelete}
        onClose={() => setToDelete(null)}
        onConfirm={async () => {
          if (!toDelete) return;
          try {
            await socialService.deleteHashtagGroup(toDelete.id);
            setToDelete(null);
            await load();
          } catch (err) {
            setError(err);
          }
        }}
        title="Excluir grupo"
        message={`Excluir o grupo "${toDelete?.name ?? ''}"? Posts que já usaram as hashtags não mudam.`}
        confirmText="Excluir"
        severity="danger"
      />
      <ApiErrorModal error={error} onClose={() => setError(null)} />
    </div>
  );
};

export default SocialHashtagsView;
